import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Loader2, Search } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatDateTime } from '@/lib/format';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * Project lists for the Documents and AI pages: the Upload drawer's Project field, "Link to project",
 * and the AI composer's "@project" scope. Loaded only when first needed (`enabled`), then kept.
 */
export function useProjectList(url, enabled) {
  const [state, setState] = useState({ projects: null, loading: false, error: '' });
  const startedRef = useRef(false);

  const load = useCallback(async () => {
    startedRef.current = true;
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await api.get(url);
      setState({ projects: withTwinNotes(Array.isArray(response.data) ? response.data : []), loading: false, error: '' });
    } catch (err) {
      setState({ projects: null, loading: false, error: getErrorMessage(err, 'Could not load the projects') });
    }
  }, [url]);

  useEffect(() => {
    if (enabled && !startedRef.current) load();
  }, [enabled, load]);

  return { ...state, retry: load };
}

const fold = (value) => String(value ?? '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');

/** Every word in the name, client or location; names that start with the query first. */
export function filterProjects(projects, query, limit = 50) {
  const words = fold(query).split(/\s+/).filter(Boolean);
  const list = (projects || []).filter((project) => {
    const haystack = fold([project.name, project.client, project.location].join(' '));
    return words.every((word) => haystack.includes(word));
  });
  if (words.length) {
    const first = words[0];
    list.sort((a, b) => (fold(a.name).startsWith(first) ? 0 : 1) - (fold(b.name).startsWith(first) ? 0 : 1));
  }
  return list.slice(0, limit);
}

export const projectSub = (project) => [project?.client, project?.location].filter(Boolean).join(' · ');

/**
 * Projects that share a name (two "AD Police", four "Nobu Hotel") get a `twin_note` — "created 27 Mar 2026",
 * with the time when they were created the same day — so the pickers never show two identical options.
 */
export function withTwinNotes(projects) {
  const nameKey = (project) => fold(project?.name).trim().replace(/\s+/g, ' ');
  const byName = new Map();
  (projects || []).forEach((project) => {
    const key = nameKey(project);
    byName.set(key, [...(byName.get(key) || []), project]);
  });
  return (projects || []).map((project) => {
    const twins = byName.get(nameKey(project)) || [];
    if (twins.length < 2) return project;
    const day = formatDate(project.created_at, { fallback: '' });
    const sameDay = day && twins.filter((twin) => formatDate(twin.created_at, { fallback: '' }) === day).length > 1;
    const when = sameDay ? formatDateTime(project.created_at, { fallback: '' }) : day;
    return { ...project, twin_note: when ? `created ${when}` : `ID ${String(project.id || '').slice(0, 8)}` };
  });
}

/** "client · location", plus the note that tells apart projects with the same name. */
export const projectDetail = (project) => [projectSub(project), project?.twin_note].filter(Boolean).join(' · ');

/** The detail line: "client · location" may be cut short, the twin note (the only difference) never is. */
function ProjectDetail({ project, className }) {
  const sub = projectSub(project);
  const note = project?.twin_note;
  if (!sub && !note) return null;
  return (
    <span className={cn('flex min-w-0 text-ink-3', className)} title={projectDetail(project)}>
      {sub ? <span className="min-w-0 truncate">{sub}</span> : null}
      {note ? <span className="shrink-0 whitespace-pre">{sub ? ' · ' : ''}{note}</span> : null}
    </span>
  );
}

/** One project row: name (navy when chosen), "client · location", and the status when not Active. */
export function ProjectOption({ project, selected, active, onSelect, id }) {
  const inactive = project.status && project.status !== 'Active';
  return (
    <button
      type="button"
      role="option"
      id={id}
      aria-selected={Boolean(selected)}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => onSelect(project)}
      className={cn(
        'flex w-full items-start gap-2 rounded-lg px-2.5 py-1.5 text-left outline-none hover:bg-surface-hover',
        active && 'bg-surface-hover'
      )}
    >
      <span className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center text-brand-ink">
        {selected ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className={cn('block truncate text-[13px]', selected ? 'font-semibold text-brand-ink' : 'font-medium text-ink')}>
          {project.name}
        </span>
        <ProjectDetail project={project} className="text-[11.5px]" />
      </span>
      {inactive ? <span className="mt-0.5 shrink-0 text-[11px] font-semibold text-ink-3">{project.status}</span> : null}
    </button>
  );
}

/** Search box + list (keyboard: ↑ ↓ Enter). */
export function ProjectSearchList({
  projects, loading, error, onRetry, value, onPick, placeholder = 'Search projects', autoFocus = true, testId, listId,
}) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef(null);
  const visible = useMemo(() => filterProjects(projects, query), [projects, query]);

  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => Math.min(index + 1, Math.max(visible.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (visible[active]) onPick(visible[active]);
    }
  };

  return (
    <div className="flex flex-col" data-testid={testId}>
      <label className="flex h-9 items-center gap-2 border-b border-line-2 px-3 text-ink-4">
        <Search className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <input
          autoFocus={autoFocus}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label={placeholder}
          className="h-full min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-4"
        />
      </label>
      <div ref={listRef} id={listId} role="listbox" aria-label="Projects" className="max-h-[280px] overflow-y-auto p-1.5">
        {loading ? (
          <p className="flex items-center gap-2 px-2.5 py-2 text-[12.5px] text-ink-3">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Loading projects…
          </p>
        ) : null}
        {error ? (
          <p className="px-2.5 py-2 text-[12.5px] text-status-red-ink">
            {error}{' '}
            {onRetry ? <button type="button" className="font-semibold text-brand-ink hover:underline" onClick={onRetry}>Try again</button> : null}
          </p>
        ) : null}
        {projects && !visible.length ? <p className="px-2.5 py-2 text-[12.5px] text-ink-3">No project matches “{query}”.</p> : null}
        {visible.map((project, index) => (
          <div key={project.id} data-index={index}>
            <ProjectOption
              project={project}
              selected={project.id === value}
              active={index === active}
              onSelect={onPick}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/** A form field that looks like a select: "Choose a project" → "LIV Waterside · LIV · Dubai Marina". */
export function ProjectSelectField({
  id, value, projects, loading, error, onRetry, onChange, disabled, invalid, testId,
}) {
  const [open, setOpen] = useState(false);
  const chosen = (projects || []).find((project) => project.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          aria-controls={open && id ? `${id}-listbox` : undefined}
          aria-haspopup="listbox"
          aria-invalid={invalid || undefined}
          data-testid={testId}
          className={cn(
            'flex h-9 w-full items-center gap-2 rounded-control border bg-surface px-3 text-left text-[13.5px] outline-none transition-colors focus-visible:border-[color:var(--brand-ink)] focus-visible:ring-2 focus-visible:ring-[color:var(--brand-soft-border)] disabled:cursor-not-allowed disabled:opacity-60',
            invalid ? 'border-[color:var(--status-red)]' : 'border-line'
          )}
        >
          {chosen ? (
            // What gives way, in order: "client · location" (it only takes the room left over), then the name
            // (up to 65% of the field). The twin note ("created 27 Mar 2026") — the only difference between
            // projects with the same name — always stays whole.
            <span className="flex min-w-0 flex-1 items-baseline" title={[chosen.name, projectDetail(chosen)].filter(Boolean).join(' · ')}>
              <span className="min-w-0 max-w-[65%] truncate font-semibold text-ink" data-testid={testId ? `${testId}-name` : undefined}>{chosen.name}</span>
              {projectSub(chosen) ? (
                <span className="min-w-0 max-w-max shrink-0 grow basis-0 truncate text-ink-3">
                  <span className="whitespace-pre"> · </span>{projectSub(chosen)}
                </span>
              ) : null}
              {chosen.twin_note ? <span className="shrink-0 whitespace-pre text-ink-3"> · {chosen.twin_note}</span> : null}
            </span>
          ) : (
            <span className="min-w-0 flex-1 truncate text-ink-4">{loading ? 'Loading projects…' : 'Choose a project'}</span>
          )}
          <ChevronDown className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-[280px] rounded-xl border-line bg-surface p-0 shadow-overlay">
        <ProjectSearchList
          projects={projects}
          loading={loading}
          error={error}
          onRetry={onRetry}
          value={value}
          onPick={(project) => { onChange(project); setOpen(false); }}
          testId={testId ? `${testId}-list` : undefined}
          listId={id ? `${id}-listbox` : undefined}
        />
      </PopoverContent>
    </Popover>
  );
}
